import { useState } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { usePerson } from '../contexts/PersonContext'
import { useCircleDetail, useRenameCircle } from '../hooks/useCircles'
import { useToast } from '../contexts/ToastContext'
import { CIRCLE_TYPES } from '../lib/circleCapabilities'
import { CircleRoster } from '../components/circles/CircleRoster'
import { CircleFamiliesTable, type CircleFamilyRow } from '../components/circles/CircleFamiliesTable'
import { CapabilityPanel } from '../components/circles/CapabilityPanel'
import { InviteFamilyButton } from '../components/circles/InviteFamilyButton'
import { Button } from '../components/ui/Button'
import { apiError } from '../lib/api/client'

/**
 * Parent circle view (COTC_Circles R6, DESIGN §3.3). Kids reach circles via /friends, so the kid
 * role is redirected home. Gaming circles render the anonymous roster; friend/family circles the
 * families table. The capability matrix renders for every type.
 */
export function CirclePage() {
  const { id } = useParams<{ id: string }>()
  const { role } = usePerson()
  const { data: circle, isLoading, isError, error, refetch } = useCircleDetail(id)

  if (role === 'kid') {
    return <Navigate to="/friends" replace />
  }

  if (isLoading) {
    return (
      <div className="px-10 py-9">
        <p className="font-mono text-[11px] uppercase tracking-label text-ink-faint">Loading circle…</p>
      </div>
    )
  }

  if (isError || !circle) {
    const status = apiError(error).status
    const unavailable = status === 403 || status === 404
    return (
      <div className="px-10 py-9">
        <h1 className="mb-2 font-serif text-[28px] font-medium text-ink">{unavailable ? 'Circle unavailable' : 'Could not load this circle'}</h1>
        <p className="max-w-[52ch] text-[14px] leading-relaxed text-ink-muted">
          {unavailable ? 'This circle could not be opened, or your family is not a member.' : 'Check your connection and try again.'}
        </p>
        {!unavailable && <Button className="mt-4" onClick={() => void refetch()}>Retry circle</Button>}
      </div>
    )
  }

  const meta = CIRCLE_TYPES.find((t) => t.typeName === circle.type)
  const families = (circle.families ?? []) as unknown as CircleFamilyRow[]
  // `isOwner` is an additive server field not yet declared on the shared CircleDetailDto type.
  const isOwner = (circle as { isOwner?: boolean }).isOwner ?? false

  return (
    <div className="animate-rise px-5 pb-11 pt-[34px] sm:px-10">
      <CircleHeader
        key={circle.id}
        circleId={circle.id}
        name={circle.name}
        typeLabel={meta?.label ?? circle.type}
        dot={meta?.dot ?? '#b9704f'}
        isOwner={isOwner}
      />

      <h1 className="mb-[6px] font-serif text-[32px] font-medium tracking-tight text-ink">{circle.heading}</h1>
      <p className="mb-[26px] max-w-[58ch] text-[14.5px] leading-relaxed text-ink-muted">{circle.blurb}</p>
      {circle.type === 'Gaming' && <div className="mb-5 max-w-sm"><InviteFamilyButton circleId={circle.id} /></div>}

      {circle.type === 'Gaming' ? (
        <CircleRoster circleId={circle.id} fallbackStats={circle.stats} />
      ) : (
        <CircleFamiliesTable circleId={circle.id} families={families} />
      )}

      {circle.type !== 'Gaming' && families.length > 0 && (
        <div className="mt-4">
          <InviteFamilyButton circleId={circle.id}>Invite another family</InviteFamilyButton>
        </div>
      )}

      <CapabilityPanel type={circle.type} />
    </div>
  )
}

interface CircleHeaderProps {
  circleId: string
  name: string
  typeLabel: string
  dot: string
  isOwner: boolean
}

function CircleHeader({ circleId, name, typeLabel, dot, isOwner }: CircleHeaderProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(name)
  const rename = useRenameCircle(circleId)
  const { flash } = useToast()

  function save() {
    const next = draft.trim()
    if (next === '' || next === name) {
      setEditing(false)
      setDraft(name)
      return
    }
    rename.mutate(next, {
      onSuccess: () => {
        flash('Circle renamed.')
        setEditing(false)
      },
      onError: () => flash('That name could not be saved.'),
    })
  }

  return (
    <div className="mb-[10px] flex flex-wrap items-center gap-[9px]">
      <span className="h-[9px] w-[9px] flex-none rounded-full border-2" style={{ borderColor: dot }} />
      {editing ? (
        <input
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save()
            if (e.key === 'Escape') {
              setEditing(false)
              setDraft(name)
            }
          }}
          aria-label="Circle name"
          className="rounded-input border border-line bg-paper-surface px-2 py-1 font-mono text-[12px] uppercase tracking-label text-ink outline-none focus:border-terra"
        />
      ) : (
        <span className="font-mono text-[12px] uppercase tracking-label text-ink-faint">
          {name} · {typeLabel}
        </span>
      )}
      {isOwner && !editing && (
        <button
          type="button"
          onClick={() => {
            setDraft(name)
            setEditing(true)
          }}
          className="font-mono text-[11px] uppercase tracking-label text-terra-deep hover:text-terra-hover"
        >
          Rename
        </button>
      )}
    </div>
  )
}
